/**
 * ProfileStats — stats row: Following · Followers · Posts · Cherished, plus an
 * own-only fifth cell, Invited (1.24.00 B2).
 *
 * Pure presentational: User in, JSX out. No hooks or stores.
 * Follower delta is only applied when viewing another user's profile (isOwn = false),
 * so the owner sees a stable count that doesn't include their own pending follow.
 *
 * Following and Followers are buttons when `onOpenConnections` is given: they open the
 * list behind the number (ConnectionsDialog). The other cells are plain numbers.
 *
 * `Invited` renders ONLY when `user.invited` is a number — the backend puts it
 * in the private (owner-only) block of serialize_user, so another member's
 * payload never carries it and the cell never appears there by construction.
 * The `isOwn` check is belt-and-braces on top of that.
 */
import styled from 'styled-components';
import { formatCount } from '@/components/formatCount';
import type { User } from '@/core/models';

/* ── Styled components ──────────────────────────────────────────────────── */

const StatsRoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin: 0;
  padding: 16px 0;
  border-block: 1px solid ${({ theme }) => theme.color.glassBorder};
  line-height: ${({ theme }) => theme.lineHeight.sm};
`;

const StatCell = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

/* The same cell as a button: no chrome at rest, an underline on hover, the focus ring
   on keyboard focus. */
const StatButton = styled.button`
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:hover span:last-child {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 4px;
  }
`;

const StatValue = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const StatLabel = styled.span`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Component ──────────────────────────────────────────────────────────── */

export interface ProfileStatsProps {
  user: User;
  isOwn: boolean;
  followerDelta: number;
  /** Open the list behind Following / Followers. Omit to render plain numbers. */
  onOpenConnections?: (kind: 'followers' | 'following') => void;
}

export function ProfileStats({ user, isOwn, followerDelta, onOpenConnections }: ProfileStatsProps) {
  const following = user.stats?.following ?? 0;
  const followers = (user.stats?.followers ?? 0) + (isOwn ? 0 : followerDelta);
  const posts = user.stats?.posts ?? 0;
  const likes = user.stats?.likes ?? 0;

  const cells: Array<{ value: number; label: string; opens?: 'followers' | 'following' }> = [
    { value: following, label: 'Following', opens: 'following' },
    { value: followers, label: 'Followers', opens: 'followers' },
    { value: posts, label: 'Posts' },
    { value: likes, label: 'Cherished' },
  ];
  if (isOwn && typeof user.invited === 'number') {
    cells.push({ value: user.invited, label: 'Invited' });
  }

  return (
    <StatsRoot>
      {cells.map(({ value, label, opens }) =>
        opens && onOpenConnections ? (
          <StatButton
            key={label}
            type="button"
            aria-label={`${formatCount(value)} ${label}. Show the list`}
            onClick={() => onOpenConnections(opens)}
          >
            <StatValue>{formatCount(value)}</StatValue>
            <StatLabel>{label}</StatLabel>
          </StatButton>
        ) : (
          <StatCell key={label}>
            <StatValue>{formatCount(value)}</StatValue>
            <StatLabel>{label}</StatLabel>
          </StatCell>
        ),
      )}
    </StatsRoot>
  );
}
